import React, { useState } from 'react';
import { Button, Box, InputAdornment, Typography } from '@mui/material';
import { AccountCircle, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { signIn, confirmSignIn, getCurrentUser } from 'aws-amplify/auth';
import BWITextField from '../shared/BWITextField';
import { useSession } from '../../context/SessionContext';

// Define user session type
interface UserSession {
  signInStep: string;
}

// Define props for input forms
interface LoginFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  handleLogin: () => void;
}

interface NewPasswordFormProps {
  newPassword: string;
  setNewPassword: (password: string) => void;
  handleConfirmNewPassword: () => void;
}

const LoginContainer: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [requireNewPassword, setRequireNewPassword] = useState<boolean>(false);
  const [userSession, setUserSession] = useState<UserSession | null>(null);
  const navigate = useNavigate();
  const { loginUser } = useSession(); 

  const handleSuccessfulLogin = async () => {
    try {
      const currentUser = await getCurrentUser();

      await loginUser(currentUser.userId); // Use the context function
      navigate('/');
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error updating user. Please check the logs.');
    }
  };

  const handleLogin = async (): Promise<void> => {
    try {
      const UserPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID || ''
      const ClientId = process.env.REACT_APP_COGNITO_CLIENT_ID || '' 
      
      const { nextStep } = await signIn({ username: email, password });

      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setRequireNewPassword(true);
        setUserSession(nextStep);
      } else {
        await handleSuccessfulLogin();
      }
    } catch (error) {
      console.error('Invalid credentials:', error);
      alert('Invalid email or password. Please try again.');
    }
  };

  const handleConfirmNewPassword = async (): Promise<void> => {
    if (!userSession) return;

    try {
      await confirmSignIn({ challengeResponse: newPassword });
      await handleSuccessfulLogin();
    } catch (error) {
      console.error('Failed to confirm new password:', error);
      alert('Failed to set new password. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        maxWidth: 600,
        backgroundColor: 'background.paper',
        padding: 3,
        borderRadius: 2,
        boxShadow: 3,
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        marginTop: '3vh',
      }}
    >
      {requireNewPassword ? (
        <NewPasswordForm
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          handleConfirmNewPassword={handleConfirmNewPassword}
        />
      ) : (
        <LoginForm
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          handleLogin={handleLogin}
        />
      )}
    </Box>
  );
};

const LoginForm: React.FC<LoginFormProps> = ({ email, setEmail, password, setPassword, handleLogin }) => (
  <>
    <BWITextField
      label="Email"
      value={email}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value.toLocaleLowerCase())}
      fullWidth
      margin="normal"
      startIcon={<AccountCircle />}
      data-cy="login-email"
    />
    <BWITextField
      label="Password"
      type="password"
      value={password}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
      fullWidth
      margin="normal"
      startIcon={<Lock />}
      data-cy="login-password"
    />
    <Button
      variant="contained"
      onClick={handleLogin}
      sx={{
        backgroundColor: '#d32f2f',
        color: '#fff',
        marginTop: 2,
        width: '100%',
        '&:hover': {
          backgroundColor: '#b71c1c',
        },
      }}
      data-cy="login-submit"
    >
      Log In
    </Button>
  </>
);

const NewPasswordForm: React.FC<NewPasswordFormProps> = ({ newPassword, setNewPassword, handleConfirmNewPassword }) => (
  <>
    <Typography variant="h6" gutterBottom>
      Set a New Password
    </Typography>
    <BWITextField
      label="New Password"
      type="password"
      value={newPassword}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
      fullWidth
      margin="normal"
      startIcon={<Lock />}
      data-cy="new-password"
    />
    <Button
      variant="contained"
      onClick={handleConfirmNewPassword}
      sx={{
        backgroundColor: '#d32f2f',
        color: '#fff',
        marginTop: 2,
        width: '100%',
        '&:hover': {
          backgroundColor: '#b71c1c',
        },
      }}
      data-cy="new-password-submit"
    >
      Confirm New Password
    </Button>
  </>
);

export default LoginContainer;
